import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserPlus, UserCheck, UserX, Shield, Search,
  Edit2, Trash2, Power, PowerOff, Activity,
  RefreshCw, ChevronLeft, ChevronRight, X, CheckCircle, XCircle,
  AlertTriangle, Mail
} from 'lucide-react';
import { useToast } from './ui/Toast';
import './PlatformTeam.css';

const API_BASE = '/api';

const api = (path, opts = {}) =>
  fetch(API_BASE + path, {
    credentials: 'include',
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) }
  });

const ROLES = ['ADMIN', 'TECH_SUPPORT', 'MARKETING', 'ACCOUNTING', 'CUSTOMER_SUPPORT'];

const ROLE_LABELS = {
  ADMIN: 'Admin',
  TECH_SUPPORT: 'Tech Support',
  MARKETING: 'Marketing',
  ACCOUNTING: 'Accounting',
  CUSTOMER_SUPPORT: 'Customer Support'
};

function getInitials(email, username) {
  if (username) {
    const parts = username.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return username.slice(0, 2).toUpperCase();
  }
  if (email) {
    const local = email.split('@')[0];
    if (local.includes('.')) {
      const parts = local.split('.');
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return local.slice(0, 2).toUpperCase();
  }
  return '??';
}

function timeAgo(dateStr) {
  if (!dateStr) return 'Never';
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function PlatformTeam() {
  const toast = useToast();

  // Data states
  const [staff, setStaff] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null); // userId of action in progress

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Audit pagination
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const AUDIT_LIMIT = 15;

  // Modal states
  const [roleModal, setRoleModal] = useState(null); // { userId, email, currentRole }
  const [selectedRole, setSelectedRole] = useState('');
  const [removeModal, setRemoveModal] = useState(null); // { userId, email }
  const [inviteModal, setInviteModal] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', staffRole: 'TECH_SUPPORT', message: '' });

  // =============================================
  // Data fetching
  // =============================================

  const fetchStaff = useCallback(async () => {
    try {
      const res = await api('/admin/rbac/staff');
      const data = await res.json();
      if (data.success) {
        setStaff(data.staff || []);
      }
    } catch (err) {
      console.error('Failed to fetch staff:', err);
    }
  }, []);

  const fetchAuditLogs = useCallback(async (page = 1) => {
    try {
      setAuditLoading(true);
      const res = await api(`/admin/rbac/audit?page=${page}&limit=${AUDIT_LIMIT}`);
      const data = await res.json();
      if (data.success) {
        setAuditLogs(data.auditLogs || []);
        setAuditTotal(data.pagination?.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchStaff(), fetchAuditLogs(1)]);
      setLoading(false);
    };
    loadAll();
  }, [fetchStaff, fetchAuditLogs]);

  useEffect(() => {
    fetchAuditLogs(auditPage);
  }, [auditPage, fetchAuditLogs]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchStaff(), fetchAuditLogs(auditPage)]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  // =============================================
  // Actions
  // =============================================

  const handleDeactivate = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await api(`/admin/rbac/staff/${userId}/deactivate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Staff member deactivated');
        await fetchStaff();
      } else {
        toast.error(data.error || 'Failed to deactivate');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (userId) => {
    setActionLoading(userId);
    try {
      const res = await api(`/admin/rbac/staff/${userId}/reactivate`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        toast.success('Staff member reactivated');
        await fetchStaff();
      } else {
        toast.error(data.error || 'Failed to reactivate');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleChangeRole = async () => {
    if (!roleModal || !selectedRole) return;
    setActionLoading(roleModal.userId);
    try {
      const res = await api(`/admin/rbac/staff/${roleModal.userId}/role`, {
        method: 'POST',
        body: JSON.stringify({ staffRole: selectedRole })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Role changed to ${ROLE_LABELS[selectedRole]}`);
        setRoleModal(null);
        setSelectedRole('');
        await fetchStaff();
      } else {
        toast.error(data.error || 'Failed to change role');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async () => {
    if (!removeModal) return;
    setActionLoading(removeModal.userId);
    try {
      const res = await api(`/admin/rbac/staff/${removeModal.userId}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        toast.success('Staff member removed');
        setRemoveModal(null);
        await fetchStaff();
      } else {
        toast.error(data.error || 'Failed to remove');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleInvite = async () => {
    if (!inviteData.email || !inviteData.staffRole) {
      toast.error('Email and role are required');
      return;
    }
    setActionLoading('invite');
    try {
      const res = await api('/admin/rbac/staff/invite', {
        method: 'POST',
        body: JSON.stringify(inviteData)
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Invitation sent successfully');
        setInviteModal(false);
        setInviteData({ email: '', staffRole: 'TECH_SUPPORT', message: '' });
        await fetchStaff();
      } else {
        toast.error(data.error || 'Failed to send invitation');
      }
    } catch (err) {
      toast.error('Network error');
    } finally {
      setActionLoading(null);
    }
  };

  // =============================================
  // Computed values
  // =============================================

  const filteredStaff = staff.filter(m => {
    const matchesSearch = !searchTerm ||
      m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (m.username && m.username.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = !roleFilter || m.staffRole === roleFilter;
    const matchesStatus = !statusFilter ||
      (statusFilter === 'active' && m.isActive !== false) ||
      (statusFilter === 'inactive' && m.isActive === false);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const totalMembers = staff.length;
  const activeMembers = staff.filter(m => m.isActive !== false).length;
  const inactiveMembers = totalMembers - activeMembers;
  const uniqueRoles = new Set(staff.map(m => m.staffRole)).size;
  const auditPages = Math.ceil(auditTotal / AUDIT_LIMIT);

  // =============================================
  // Render
  // =============================================

  if (loading) {
    return (
      <div className="pt-page">
        <div className="pt-loading">
          <div className="pt-spinner" />
          <span>Loading platform team...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-page">
      {/* Header */}
      <div className="pt-header">
        <div className="pt-header-left">
          <h1>Platform Team</h1>
          <p>Manage platform-level staff and admin users</p>
        </div>
        <button className="pt-invite-btn" onClick={() => setInviteModal(true)}>
          <UserPlus size={16} />
          Invite Member
        </button>
      </div>

      {/* Stat Cards */}
      <div className="pt-stats-grid">
        <div className="pt-stat-card">
          <div>
            <div className="pt-stat-label">Total Members</div>
            <div className="pt-stat-value">{totalMembers}</div>
          </div>
          <div className="pt-stat-icon pt-icon-total">
            <Users size={20} />
          </div>
        </div>
        <div className="pt-stat-card">
          <div>
            <div className="pt-stat-label">Active</div>
            <div className="pt-stat-value">{activeMembers}</div>
          </div>
          <div className="pt-stat-icon pt-icon-active">
            <UserCheck size={20} />
          </div>
        </div>
        <div className="pt-stat-card">
          <div>
            <div className="pt-stat-label">Inactive</div>
            <div className="pt-stat-value">{inactiveMembers}</div>
          </div>
          <div className="pt-stat-icon pt-icon-inactive">
            <UserX size={20} />
          </div>
        </div>
        <div className="pt-stat-card">
          <div>
            <div className="pt-stat-label">Roles</div>
            <div className="pt-stat-value">{uniqueRoles}</div>
          </div>
          <div className="pt-stat-icon pt-icon-roles">
            <Shield size={20} />
          </div>
        </div>
      </div>

      {/* Staff Members Section */}
      <div className="pt-section">
        <div className="pt-section-header">
          <h3 className="pt-section-title">
            <Users size={18} />
            Team Members
            <span className="pt-section-count">{filteredStaff.length}</span>
          </h3>
          <button
            className={`pt-refresh-btn ${refreshing ? 'pt-spinning' : ''}`}
            onClick={handleRefresh}
            title="Refresh"
          >
            <RefreshCw size={14} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="pt-toolbar">
          <div className="pt-search-wrap">
            <Search size={14} />
            <input
              type="text"
              className="pt-search-input"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="pt-filter-select"
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            {ROLES.map(r => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
          <select
            className="pt-filter-select"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Member List */}
        <div className="pt-member-list">
          {filteredStaff.length === 0 ? (
            <div className="pt-empty">
              <Users size={40} />
              <div className="pt-empty-title">No team members found</div>
              <div className="pt-empty-desc">
                {searchTerm || roleFilter || statusFilter
                  ? 'Try adjusting your filters'
                  : 'Invite your first team member to get started'}
              </div>
            </div>
          ) : (
            filteredStaff.map(member => (
              <div
                key={member._id}
                className={`pt-member-card ${member.isActive === false ? 'pt-member-inactive' : ''}`}
              >
                {/* Avatar */}
                <div className={`pt-avatar pt-avatar-${member.staffRole}`}>
                  {getInitials(member.email, member.username)}
                </div>

                {/* Info */}
                <div className="pt-member-info">
                  <div className="pt-member-name">
                    {member.username || member.email.split('@')[0]}
                  </div>
                  <div className="pt-member-email">{member.email}</div>
                </div>

                {/* Role Badge */}
                <span className={`pt-role-badge pt-role-${member.staffRole}`}>
                  <Shield size={10} />
                  {ROLE_LABELS[member.staffRole] || member.staffRole}
                </span>

                {/* Status */}
                <div className="pt-member-status">
                  <span className={`pt-status-badge ${member.isActive === false ? 'pt-status-inactive' : 'pt-status-active'}`}>
                    <span className={`pt-status-dot ${member.isActive === false ? 'pt-status-dot-inactive' : 'pt-status-dot-active'}`} />
                    {member.isActive === false ? 'Inactive' : 'Active'}
                  </span>
                  <span className="pt-last-seen">
                    {member.lastLogin ? `Last: ${timeAgo(member.lastLogin)}` : 'Never logged in'}
                  </span>
                </div>

                {/* Actions */}
                <div className="pt-member-actions">
                  <button
                    className="pt-action-btn"
                    onClick={() => {
                      setRoleModal({ userId: member._id, email: member.email, currentRole: member.staffRole });
                      setSelectedRole(member.staffRole);
                    }}
                    disabled={actionLoading === member._id}
                    title="Change Role"
                  >
                    <Edit2 size={13} />
                    <span>Role</span>
                  </button>

                  {member.isActive === false ? (
                    <button
                      className="pt-action-btn pt-action-success"
                      onClick={() => handleReactivate(member._id)}
                      disabled={actionLoading === member._id}
                      title="Reactivate"
                    >
                      <Power size={13} />
                      <span>Activate</span>
                    </button>
                  ) : (
                    <button
                      className="pt-action-btn pt-action-danger"
                      onClick={() => handleDeactivate(member._id)}
                      disabled={actionLoading === member._id}
                      title="Deactivate"
                    >
                      <PowerOff size={13} />
                      <span>Deactivate</span>
                    </button>
                  )}

                  <button
                    className="pt-action-btn pt-action-danger"
                    onClick={() => setRemoveModal({ userId: member._id, email: member.email })}
                    disabled={actionLoading === member._id}
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Activity Log Section */}
      <div className="pt-section">
        <div className="pt-section-header">
          <h3 className="pt-section-title">
            <Activity size={18} />
            Activity Log
            {auditTotal > 0 && <span className="pt-section-count">{auditTotal} total</span>}
          </h3>
        </div>

        <div className="pt-activity-list">
          {auditLoading ? (
            <div className="pt-loading" style={{ padding: '32px 0' }}>
              <div className="pt-spinner" />
              <span>Loading activity...</span>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="pt-empty">
              <Activity size={36} />
              <div className="pt-empty-title">No activity yet</div>
              <div className="pt-empty-desc">Admin actions will appear here as they happen</div>
            </div>
          ) : (
            auditLogs.map(log => (
              <div key={log.id} className="pt-activity-item">
                <div className={`pt-activity-icon ${
                  log.result === 'denied' || (log.statusCode && log.statusCode >= 400)
                    ? 'pt-activity-error'
                    : log.method === 'POST' || log.method === 'PUT' || log.method === 'DELETE'
                      ? 'pt-activity-info'
                      : 'pt-activity-success'
                }`}>
                  {log.result === 'denied' || (log.statusCode && log.statusCode >= 400)
                    ? <XCircle size={14} />
                    : log.method === 'DELETE'
                      ? <Trash2 size={14} />
                      : log.method === 'POST' || log.method === 'PUT'
                        ? <Edit2 size={14} />
                        : <CheckCircle size={14} />
                  }
                </div>
                <div className="pt-activity-content">
                  <div className="pt-activity-text">
                    <strong>{log.userEmail || 'Unknown user'}</strong>
                    {' '}{formatAction(log.action)}
                    {log.path && <span style={{ color: 'var(--text-light)' }}> {log.path}</span>}
                  </div>
                  <div className="pt-activity-meta">
                    <span className={`pt-activity-method pt-method-${log.method}`}>
                      {log.method}
                    </span>
                    <span>{timeAgo(log.timestamp)}</span>
                    {log.ip && <span>{log.ip}</span>}
                    {log.duration != null && <span>{log.duration}ms</span>}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Audit Pagination */}
        {auditPages > 1 && (
          <div className="pt-pagination">
            <button
              className="pt-page-btn"
              disabled={auditPage <= 1}
              onClick={() => setAuditPage(p => Math.max(1, p - 1))}
            >
              <ChevronLeft size={14} />
            </button>
            <span className="pt-page-info">
              Page {auditPage} of {auditPages}
            </span>
            <button
              className="pt-page-btn"
              disabled={auditPage >= auditPages}
              onClick={() => setAuditPage(p => p + 1)}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* =============================================
          MODALS
          ============================================= */}

      {/* Change Role Modal */}
      {roleModal && (
        <div className="pt-modal-overlay" onClick={() => setRoleModal(null)}>
          <div className="pt-modal" onClick={e => e.stopPropagation()}>
            <div className="pt-modal-header">
              <h3>Change Role</h3>
              <button className="pt-modal-close" onClick={() => setRoleModal(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="pt-modal-body">
              <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: '0 0 20px' }}>
                Update the role for <strong>{roleModal.email}</strong>
              </p>
              <div className="pt-form-group">
                <label className="pt-form-label">New Role</label>
                <select
                  className="pt-form-select"
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="pt-modal-footer">
              <button className="pt-btn pt-btn-secondary" onClick={() => setRoleModal(null)}>
                Cancel
              </button>
              <button
                className="pt-btn pt-btn-primary"
                onClick={handleChangeRole}
                disabled={selectedRole === roleModal.currentRole || actionLoading === roleModal.userId}
              >
                {actionLoading === roleModal.userId ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Confirmation Modal */}
      {removeModal && (
        <div className="pt-modal-overlay" onClick={() => setRemoveModal(null)}>
          <div className="pt-modal" onClick={e => e.stopPropagation()}>
            <div className="pt-modal-header">
              <h3>Remove Staff Member</h3>
              <button className="pt-modal-close" onClick={() => setRemoveModal(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="pt-modal-body">
              <p className="pt-confirm-text">
                Are you sure you want to remove <strong>{removeModal.email}</strong> from the platform team?
              </p>
              <div className="pt-confirm-warning">
                <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>This will revoke all admin access. The user account will be converted to a regular merchant account.</span>
              </div>
            </div>
            <div className="pt-modal-footer">
              <button className="pt-btn pt-btn-secondary" onClick={() => setRemoveModal(null)}>
                Cancel
              </button>
              <button
                className="pt-btn pt-btn-danger"
                onClick={handleRemove}
                disabled={actionLoading === removeModal.userId}
              >
                <Trash2 size={14} />
                {actionLoading === removeModal.userId ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {inviteModal && (
        <div className="pt-modal-overlay" onClick={() => setInviteModal(false)}>
          <div className="pt-modal" onClick={e => e.stopPropagation()}>
            <div className="pt-modal-header">
              <h3>Invite Team Member</h3>
              <button className="pt-modal-close" onClick={() => setInviteModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="pt-modal-body">
              <div className="pt-form-group">
                <label className="pt-form-label">Email Address</label>
                <input
                  type="email"
                  className="pt-form-input"
                  placeholder="staff@company.com"
                  value={inviteData.email}
                  onChange={e => setInviteData(d => ({ ...d, email: e.target.value }))}
                />
              </div>
              <div className="pt-form-group">
                <label className="pt-form-label">Role</label>
                <select
                  className="pt-form-select"
                  value={inviteData.staffRole}
                  onChange={e => setInviteData(d => ({ ...d, staffRole: e.target.value }))}
                >
                  {ROLES.map(r => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
              </div>
              <div className="pt-form-group" style={{ marginBottom: 0 }}>
                <label className="pt-form-label">Message (optional)</label>
                <textarea
                  className="pt-form-textarea"
                  placeholder="Add a personal welcome message..."
                  value={inviteData.message}
                  onChange={e => setInviteData(d => ({ ...d, message: e.target.value }))}
                />
              </div>
            </div>
            <div className="pt-modal-footer">
              <button className="pt-btn pt-btn-secondary" onClick={() => setInviteModal(false)}>
                Cancel
              </button>
              <button
                className="pt-btn pt-btn-primary"
                onClick={handleInvite}
                disabled={!inviteData.email || actionLoading === 'invite'}
              >
                <Mail size={14} />
                {actionLoading === 'invite' ? 'Sending...' : 'Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Formats an action string like "merchants:read" into human-readable text.
 */
function formatAction(action) {
  if (!action) return 'performed an action';
  const parts = action.split(':');
  const resource = parts[0] || 'unknown';
  const verb = parts[1] || 'accessed';

  const verbMap = {
    read: 'viewed',
    write: 'updated',
    update: 'updated',
    delete: 'deleted',
    login: 'logged in',
    logout: 'logged out',
    create: 'created'
  };

  const readableVerb = verbMap[verb] || verb;
  if (verb === 'login' || verb === 'logout') return readableVerb;
  return `${readableVerb} ${resource}`;
}

export default PlatformTeam;
